<script>
  import Page from './Page.svelte'
  import URLPattern from 'url-pattern'
  import _ from 'lodash'
  import { Resource, Path, Query, Parameters, Fragment, Values } from './Router.js'
  import { 
    extractPath, 
    extractQuery, 
    extractParameters, 
    extractFragment,
    navigate as _navigate,
    navigateOffsite as _navigateOffsite,
  } from './Router.js'
  import { log, warn, error, publish, subscribe } from '@coldandgoji/utilities'
  import { onMount } from 'svelte'

  export let initialized = true
  export let routes

  let current = {
    back: false,
    route: {},
    resource: null,
  }

  let skip = false

  /**
   * resolveValues
   *
   * Resolves the given resource string's parameterized
   * values with no concern for guards or further resolution.
   * Exists to profer up the resources values for initialization.
   */
  const resolveValues = resource => {
    log( '@coldandgoji/svelte-router', 'resolveValues', [ `Resource: ${ resource }` ] )

    const match = _.find( routes, route => {
      const pattern = new URLPattern( route.pattern || route.path )

      let patternMatch = pattern.match( extractPath( resource ) )

      if ( patternMatch ) {
        Values.set( patternMatch )        
        return true
      }
    } )
  }

  /**
   * resolve
   * 
   * Accepts a resource and parses the available routes, 
   * returning the eventual route
   */
  const resolve = resource => {
    log( '@coldandgoji/svelte-router', 'resolve', resource )

    // Ensure parameters
    if ( !resource ) {
      error( '@coldandgoji/svelte-router', 'resolve', 'Missing parameter: resource' )
      return null
    }

    // Get path from resource parameter
    const path = extractPath( resource )
    log( '@coldandgoji/svelte-router', 'resolve > path', path )

    // Get query string from resource parameter
    const query = extractQuery( resource )
    log( '@coldandgoji/svelte-router', 'resolve > query', query )

    // Get parameters from resource parameter
    const parameters = extractParameters( resource )
    log( '@coldandgoji/svelte-router', 'resolve > parameters', parameters )

    // Get fragment from resource parameter
    const fragment = extractFragment( resource )
    log( '@coldandgoji/svelte-router', 'resolve > fragment', fragment )

    // Find matching route via pattern or path
    const match = _.find( routes, route => {
      const pattern = new URLPattern( route.pattern || route.path )

      let patternMatch = pattern.match( extractPath( resource ) )

      if ( patternMatch ) {
        Values.set( patternMatch )        

        route.path = path
        return true
      }
    } )

    // If match, resolve
    if ( match ) {
      log( '@coldandgoji/svelte-router', 'resolve > match', match )
      // If redirect, follow and recurse call, passing along
      // query string and fragment if they exist
      if ( match.redirect ) {
        log( '@coldandgoji/svelte-router', 'resolve > redirect', match.redirect )

        let redirect = _.isFunction( match.redirect )
          ? match.redirect()
          : match.redirect

        if ( query ) {
          redirect += `?${ query }`
        }

        if ( fragment ) {
          redirect += `#${ fragment }`
        }

        log( '@coldandgoji/svelte-router', 'resolve > recursing redirect', redirect )
        return resolve( redirect )
      }

      // Check if found route has guard, and if valid,
      // redirect
      const resolveGuard = guard => {
        if ( guard.trap() ) {
          log( '@coldandgoji/svelte-router', 'resolve > guard', guard )

          let redirect = _.isFunction( guard.redirect )
            ? guard.redirect()
            : guard.redirect

          if ( query ) {
            redirect += `?${ query }`
          }

          if ( fragment ) {
            redirect += `#${ fragment }`
          }


          log( '@coldandgoji/svelte-router', 'resolve > recursing guard', redirect )
          return redirect
        }
      }

      if ( match.guard ) {
        let redirect = null
        if ( _.isArray( match.guard ) ) {
          for ( let guard of match.guard ) {
            redirect = resolveGuard( guard )
            if( redirect ) break
          }
        } else {
          redirect = resolveGuard( match.guard )
        }

        if ( redirect ) {
          return resolve( redirect )
        }
      }

      // Construct return resource
      let _resource = path

      // Check if found route has any allowed parameters
      if ( match.query ) {

        // Create new search params instance
        const _query = new URLSearchParams()

        // Iterate through allowed parameters and attach them 
        // from the query store
        _.each( match.query, parameter => {
          // If allowed parameter exists in Query store, set it
          if ( parameters[ parameter ] ) {
            _query.set( parameter, parameters[ parameter ] )
          }
        } )

        if ( _query.toString().length ) {
          _resource += `?${ _query }`
        }
      }

      if ( fragment ) {
        _resource += `#${ fragment }`
      }

      return { 
        route: match,
        resource: _resource
      }
    }

    // If no match, error
    if ( !match ) {
      error( '@coldandgoji/svelte-router', 'Route had no matches and no wildcard setup' )
    }
  }

  export const navigate = ( resource = $Resource ) => {
    log( '@coldandgoji/svelte-router', 'navigate', resource )

    current = resolve( resource )

    log( '@coldandgoji/svelte-router', 'navigate > current', current )

    publish( 'router.navigate', current )

    // If newly found resource does not equal the resource found in the Router store, set to keep in sync
    if ( $Resource !== current.resource ) {
      $Resource = current.resource

      resolveValues( current.resource )

      history.replaceState( { resource: current.resource }, null, current.resource )
      return
    } else {
      if ( current.back ) {
        log( '@coldandgoji/svelte-router', 'navigate > back', current.resource )

        resolveValues( current.resource )

        history.replaceState( { resource: current.resource }, null, current.resource )

        current.back = false
      } else {
        log( '@coldandgoji/svelte-router', 'navigate > pushState', current.resource )

        resolveValues( current.resource )

        history.pushState( { resource: current.resource }, null, current.resource )
      }
    }
  }

  const back = resource => {
    log( '@coldandgoji/svelte-router', 'back', resource )
    current.back = true    

    $Resource = resource
  }

  // This only runs on instantiation - before initialization -
  // so that the URL values, if any, are available during the 
  // initialization script. It never runs again.
  resolveValues( $Resource )

  subscribe( 'Router.navigate', () => navigate() )

  const handleClick = event => {
    if ( event.target.tagName === 'A' ) {
      // If clicking on *any* A tag, prevent default and
      // check the hostname. If the link is internal,
      // navigate - otherwise navigate offsite.
      event.preventDefault()

      event.target.host === location.host
        ? _navigate( event.target.getAttribute( 'href' ) )
        : _navigateOffsite( event.target.href )
    }
  }

  $: initialized && $Resource && navigate()

  $: document.title = _.get( current.route, 'meta.title', 'Default Title' )
</script>

<svelte:window 
  on:click={ handleClick }
  on:popstate="{ event => back( event.state.resource ) }" 
/>

<Page route={ current.route }>
  <svelte:component this={ current.route.page } />
</Page>
